// Tooltip.tsx
import React, { useState, useRef, useLayoutEffect } from "react";
import ReactDOM from "react-dom";

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  skip?: boolean;
  paddingTop?: number;
  width?: React.CSSProperties["width"];
  /**
   * Controlled visibility, for feedback that must show without a hover (touch screens,
   * validation messages). When set, hover is ignored.
   */
  open?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
  text,
  children,
  skip = false,
  paddingTop = 8,
  width,
  open,
}) => {
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipHight, setTooltipHight] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const isControlled = open !== undefined;
  const isVisible = isControlled ? open : isTooltipVisible;

  const updatePosition = () => {
    const rect = tooltipRef.current?.getBoundingClientRect() as DOMRect;
    if (tooltipRef.current) {
      setPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX + rect.width / 2,
      });
    }
  };

  const handleMouseEnter = () => {
    if (isControlled) return;
    updatePosition();
    setIsTooltipVisible(true);
  };

  const handleMouseLeave = () => {
    setIsTooltipVisible(false);
  };

  // A controlled tooltip has no mouseenter to position it, so position on opening.
  useLayoutEffect(() => {
    if (isControlled && open) updatePosition();
  }, [isControlled, open]);

  // `ref.current?.offsetHeight` as a DEPENDENCY forced a synchronous layout read
  // on every single render, and the effect then set state from that same value —
  // a read/write loop that is a classic layout-thrash source. The height only
  // matters while the tooltip is open (and changes only with its text), so measure
  // on those transitions instead.
  useLayoutEffect(() => {
    if (!isVisible) return;
    const h = ref.current?.offsetHeight;
    if (h) setTooltipHight(h);
  }, [isVisible, text]);
  if (skip) {
    return children;
  }
  return (
    <>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ position: "relative" }}
        ref={tooltipRef}
      >
        {children}
      </div>
      {isVisible &&
        ReactDOM.createPortal(
          <div
            className="tooltip-container"
            ref={ref}
            style={{
              top: position.top - tooltipHight - paddingTop,
              left: position.left,
              whiteSpace: "pre-wrap",
              width,
            }}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
};

export default Tooltip;
